/**************
 Landing page motion helpers
 ***************/

/*
 Shared, self-initialising singleton for the three motion effects the landing-page sections need.
 Loaded once from layout/theme.landing.liquid rather than per-section, so all sections share one
 IntersectionObserver and one scroll listener instead of N of each.

 The theme has no animation library (no GSAP/AOS/Lenis/ScrollTimeline anywhere) and no bundler, so
 this is hand-rolled to match — adding a dependency would make it the only outlier in the repo.
 The IntersectionObserver + requestIdleCallback shape follows assets/theme-global.js:564, and the
 reduced-motion discipline follows sections/section-marquee-v1.liquid.

 Every effect degrades to its finished state rather than its starting state under
 prefers-reduced-motion, so nothing is ever left invisible or stuck at zero.

 Markup contracts
 ----------------
 Reveal:   <div data-lp-reveal>                 -> gains .lpRevealed once ~15% visible
           <div data-lp-reveal data-lp-reveal-delay="120">   -> staggered by 120ms
 Parallax: <img data-lp-parallax="0.12">        -> translateY of (rate * distance from centre)
                                                   Desktop only, see PARALLAX_MIN_WIDTH.
 Counter:  <span data-lp-counter data-lp-counter-annual="42432000">
                                                -> year-to-date share of an annual total, ticking
*/

window.lpMotion = window.lpMotion || (function () {
    "use strict";

    const reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    // parallax is desktop-only: on low-end Android the scroll-linked transform costs more in
    // jank than the effect is worth, and the mobile layouts stack the art anyway
    const PARALLAX_MIN_WIDTH = 801;

    // requestIdleCallback shim, same shape as theme-global.js:564
    const whenIdle = window.requestIdleCallback
        ? cb => window.requestIdleCallback(cb, {timeout: 2000})
        : cb => setTimeout(cb, 200);

    function prefersReducedMotion() {
        return reduceMotionQuery.matches;
    }

    /**************
     Reveal
     ***************/

    let revealObserver = null;

    function revealObserverInstance() {
        if (revealObserver) {
            return revealObserver;
        }
        revealObserver = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) {
                    return;
                }
                const delay = parseInt(entry.target.dataset.lpRevealDelay, 10);
                if (delay > 0) {
                    setTimeout(() => entry.target.classList.add("lpRevealed"), delay);
                } else {
                    entry.target.classList.add("lpRevealed");
                }
                // one-shot: a revealed element never needs watching again
                revealObserver.unobserve(entry.target);
            });
        }, {threshold: 0.15, rootMargin: "0px 0px -5% 0px"});
        return revealObserver;
    }

    function initReveal(root) {
        const targets = root.querySelectorAll("[data-lp-reveal]:not([data-lp-reveal-bound])");
        targets.forEach(function (element) {
            element.dataset.lpRevealBound = "true";

            // reduced motion, or no IO support: show it immediately, fully revealed
            if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
                element.classList.add("lpRevealed");
                return;
            }
            revealObserverInstance().observe(element);
        });
    }

    /**************
     Parallax
     ***************/

    const parallaxItems = [];
    let parallaxObserver = null;
    let parallaxTicking = false;
    let parallaxListening = false;

    function parallaxEnabled() {
        return !prefersReducedMotion() && window.innerWidth >= PARALLAX_MIN_WIDTH;
    }

    function paintParallax() {
        parallaxTicking = false;
        if (!parallaxEnabled()) {
            return;
        }
        const viewportCentre = window.innerHeight / 2;
        parallaxItems.forEach(function (item) {
            // only elements currently on screen are worth a layout read + style write
            if (!item.visible) {
                return;
            }
            const rect = item.element.getBoundingClientRect();
            const elementCentre = rect.top + rect.height / 2;
            const offset = (viewportCentre - elementCentre) * item.rate;
            item.element.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
        });
    }

    function requestParallaxPaint() {
        if (parallaxTicking) {
            return;
        }
        parallaxTicking = true;
        window.requestAnimationFrame(paintParallax);
    }

    function clearParallax() {
        parallaxItems.forEach(item => {
            item.element.style.transform = "";
        });
    }

    function initParallax(root) {
        const targets = root.querySelectorAll("[data-lp-parallax]:not([data-lp-parallax-bound])");
        if (!targets.length) {
            return;
        }

        if (!parallaxObserver) {
            parallaxObserver = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    const item = parallaxItems.find(candidate => candidate.element === entry.target);
                    if (item) {
                        item.visible = entry.isIntersecting;
                    }
                });
                requestParallaxPaint();
            }, {rootMargin: "10% 0px"});
        }

        targets.forEach(function (element) {
            element.dataset.lpParallaxBound = "true";
            const rate = parseFloat(element.dataset.lpParallax);
            parallaxItems.push({
                element: element,
                rate: Number.isFinite(rate) ? rate : 0.1,
                visible: false
            });
            parallaxObserver.observe(element);
        });

        if (!parallaxListening) {
            parallaxListening = true;
            window.addEventListener("scroll", requestParallaxPaint, {passive: true});
            window.addEventListener("resize", function () {
                // crossing the breakpoint (or rotating) must drop any transform left behind,
                // otherwise the mobile layout inherits a stale desktop offset
                if (!parallaxEnabled()) {
                    clearParallax();
                } else {
                    requestParallaxPaint();
                }
            }, {passive: true});
        }

        requestParallaxPaint();
    }

    /**************
     Counter
     ***************/

    /*
     Year-to-date share of an annual total. Deterministic — derived from the clock, never from a
     stored starting point — so a reload lands on the same number instead of restarting the count,
     and the interval below recomputes from the clock each tick so it cannot drift.
    */
    function counterValue(annual, now) {
        const year = now.getFullYear();
        const startOfYear = new Date(year, 0, 1).getTime();
        const startOfNextYear = new Date(year + 1, 0, 1).getTime();
        const elapsed = now.getTime() - startOfYear;
        const yearLength = startOfNextYear - startOfYear;
        return Math.floor(annual * (elapsed / yearLength));
    }

    function initCounter(root) {
        const targets = root.querySelectorAll("[data-lp-counter]:not([data-lp-counter-bound])");
        targets.forEach(function (element) {
            element.dataset.lpCounterBound = "true";

            const annual = parseFloat(element.dataset.lpCounterAnnual);
            if (!Number.isFinite(annual) || annual <= 0) {
                return;
            }

            function paint() {
                element.textContent = counterValue(annual, new Date()).toLocaleString();
            }

            paint();

            // a static number is the honest reduced-motion equivalent — it's still accurate,
            // it just doesn't animate
            if (prefersReducedMotion()) {
                return;
            }

            // 250ms recompute rather than rAF: the value climbs ~1-2/second, so a per-frame
            // update would repaint 60x for the same digits
            whenIdle(function () {
                setInterval(paint, 250);
            });
        });
    }

    /**************
     Count up
     ***************/

    /*
     Counts a stat up to its final value the first time it scrolls into view, once only.

     Different from the live counter above: that one is derived from the clock and ticks forever;
     this animates from zero to a fixed number and stops. The number is parsed out of the element's
     own text so "4x", "6th" and "360" all work — the prefix and suffix are preserved and only the
     digits animate. Anything with no digits is left alone.

     Under reduced motion the final value is simply left as rendered: it's already correct in the
     markup, so there is nothing to do and nothing to reveal.
    */
    let countUpObserver = null;

    function animateCountUp(element, target, prefix, suffix, decimals) {
        const duration = 1100;
        let startTime = null;

        function frame(now) {
            if (startTime === null) {
                startTime = now;
            }
            const progress = Math.min((now - startTime) / duration, 1);
            // easeOutCubic: fast off the mark, settles onto the final figure
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = target * eased;
            const shown = decimals > 0
                ? current.toFixed(decimals)
                : Math.round(current).toLocaleString();
            element.textContent = `${prefix}${shown}${suffix}`;
            if (progress < 1) {
                window.requestAnimationFrame(frame);
            } else {
                // land exactly on the authored text rather than on rounded arithmetic
                element.textContent = element.dataset.lpCountupFinal;
            }
        }

        window.requestAnimationFrame(frame);
    }

    function initCountUp(root) {
        const targets = root.querySelectorAll("[data-lp-countup]:not([data-lp-countup-bound])");
        targets.forEach(function (element) {
            element.dataset.lpCountupBound = "true";

            const original = element.textContent.trim();
            element.dataset.lpCountupFinal = original;

            // prefix / digits / suffix — "6th" -> "" "6" "th",  "4x" -> "" "4" "x"
            const match = original.match(/^(\D*)([\d.,]+)(.*)$/);
            if (!match || prefersReducedMotion() || !("IntersectionObserver" in window)) {
                return;
            }

            const prefix = match[1];
            const raw = match[2];
            const suffix = match[3];
            const target = parseFloat(raw.replace(/,/g, ""));
            if (!Number.isFinite(target)) {
                return;
            }
            const decimals = raw.includes(".") ? (raw.split(".")[1] || "").length : 0;

            // start from zero so the first paint doesn't flash the answer
            element.textContent = `${prefix}${decimals > 0 ? (0).toFixed(decimals) : "0"}${suffix}`;

            if (!countUpObserver) {
                countUpObserver = new IntersectionObserver(function (entries) {
                    entries.forEach(function (entry) {
                        if (!entry.isIntersecting) {
                            return;
                        }
                        const el = entry.target;
                        countUpObserver.unobserve(el);
                        animateCountUp(el, parseFloat(el.dataset.lpCountupTarget),
                            el.dataset.lpCountupPrefix || "", el.dataset.lpCountupSuffix || "",
                            parseInt(el.dataset.lpCountupDecimals, 10) || 0);
                    });
                }, {threshold: 0.4});
            }

            element.dataset.lpCountupTarget = target;
            element.dataset.lpCountupPrefix = prefix;
            element.dataset.lpCountupSuffix = suffix;
            element.dataset.lpCountupDecimals = decimals;
            countUpObserver.observe(element);
        });
    }

    /**************
     Autoplay video
     ***************/

    /*
     Play a looping video only while it's on screen. Cheaper than the theme's existing habit of
     autoplaying every video on the page at once (see snippets/product-visual-variant.liquid:23),
     which matters on a landing page carrying half a dozen of them.

     Deliberately still plays under prefers-reduced-motion: these are product demo loops that carry
     the section's meaning, and the markup pairs them with a poster. Anything decorative should use
     a poster-only image instead of asking this to stop.
    */
    let videoObserver = null;

    function initVideos(root) {
        const videos = root.querySelectorAll("video[data-lp-autoplay]:not([data-lp-autoplay-bound])");
        if (!videos.length) {
            return;
        }

        if (!("IntersectionObserver" in window)) {
            videos.forEach(function (video) {
                video.dataset.lpAutoplayBound = "true";
                video.play().catch(() => {});
            });
            return;
        }

        if (!videoObserver) {
            videoObserver = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (entry.isIntersecting) {
                        // preload="none" in the markup, so this is also the first byte fetched
                        entry.target.play().catch(() => {});
                    } else {
                        entry.target.pause();
                    }
                });
            }, {threshold: 0.2});
        }

        videos.forEach(function (video) {
            video.dataset.lpAutoplayBound = "true";
            videoObserver.observe(video);
        });
    }

    /**************
     Init
     ***************/

    function init(root) {
        const scope = root || document;
        initReveal(scope);
        initParallax(scope);
        initCounter(scope);
        initCountUp(scope);
        initVideos(scope);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => init(document));
    } else {
        init(document);
    }

    // theme editor: a re-rendered section arrives with unbound elements
    document.addEventListener("shopify:section:load", event => init(event.target));

    // toggling the OS setting mid-session should take effect without a reload
    reduceMotionQuery.addEventListener("change", function () {
        if (prefersReducedMotion()) {
            clearParallax();
            document.querySelectorAll("[data-lp-reveal]").forEach(el => el.classList.add("lpRevealed"));
        } else {
            requestParallaxPaint();
        }
    });

    return {
        init: init,
        counterValue: counterValue,
        prefersReducedMotion: prefersReducedMotion
    };
})();
